import React, { useState, useMemo } from "react";
import {
  Search,
  Filter,
  FileText,
  ExternalLink,
  ArrowRight,
  Shield,
  Layers,
  Sparkles,
  LayoutDashboard,
  CheckCircle2,
  FolderOpen
} from "lucide-react";
import { MASTER_89_REPORTS, REPORT_MODULES } from "./reportCatalogData";

const ReportCatalogBrowser = ({ onSelectReport, onOpenDashboard }) => {
  const [selectedModule, setSelectedModule] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const filteredReports = useMemo(() => {
    let list = MASTER_89_REPORTS;
    if (selectedModule !== "All") {
      list = list.filter((r) => r.module.toLowerCase() === selectedModule.toLowerCase());
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.module.toLowerCase().includes(q) ||
          r.columns.some((c) => c.toLowerCase().includes(q))
      );
    }
    return list;
  }, [selectedModule, searchTerm]);

  return (
    <div className="bg-[#f8fafc] min-h-screen text-slate-800 pb-16">
      {/* ── HEADER ──────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-xs">
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-xs text-slate-400 font-medium mb-1">
                <button
                  onClick={onOpenDashboard}
                  className="hover:text-slate-700 cursor-pointer"
                >
                  Dashboard
                </button>
                <span>/</span>
                <span className="text-[#b07d17] font-semibold">Reports Directory</span>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                <span>Complete 89-Report Catalog</span>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-50 text-[#b07d17] font-bold border border-amber-200">
                  89 Enterprise Definitions
                </span>
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Front-end admin reporting system covering Operations, Finance, Inventory, Sellers, Customer and Security
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onOpenDashboard}
                className="px-3.5 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <LayoutDashboard size={14} className="text-[#b07d17]" />
                <span>Executive Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 pt-5 space-y-5">
        {/* ── SEARCH & MODULE FILTER CHIPS ────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 mb-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search across all 89 reports, modules or columns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-800 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17]"
              />
            </div>

            <div className="text-xs text-slate-500 font-medium">
              Showing <span className="font-bold text-slate-900">{filteredReports.length}</span> of 89 reports
            </div>
          </div>

          {/* Module Filter Chips */}
          <div className="flex items-center gap-1.5 flex-wrap overflow-x-auto pb-1">
            {REPORT_MODULES.map((mod) => (
              <button
                key={mod}
                onClick={() => setSelectedModule(mod)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  selectedModule === mod
                    ? "bg-[#131921] text-[#febd69] shadow-xs"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200/60"
                }`}
              >
                {mod}
              </button>
            ))}
          </div>
        </div>

        {/* ── REPORT CARDS GRID ───────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredReports.map((rep) => (
            <div
              key={rep.id}
              onClick={() => onSelectReport(rep)}
              className="bg-white rounded-xl border border-slate-200/80 p-4 shadow-xs hover:shadow-md hover:border-amber-300 transition-all duration-200 cursor-pointer flex flex-col justify-between group"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                      #{String(rep.id).padStart(2, "0")}
                    </span>
                    <span className="text-[11px] font-semibold text-[#b07d17] bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                      {rep.module}
                    </span>
                  </div>
                  <ArrowRight
                    size={15}
                    className="text-slate-300 group-hover:text-[#b07d17] group-hover:translate-x-1 transition-all"
                  />
                </div>

                <h3 className="text-sm font-bold text-slate-900 group-hover:text-[#b07d17] transition-colors leading-snug">
                  {rep.name}
                </h3>

                {/* Filters preview */}
                <div className="mt-2.5">
                  <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                    Filters:
                  </p>
                  <p className="text-[11.5px] text-slate-600 truncate mt-0.5">
                    {rep.filters.join(" • ")}
                  </p>
                </div>

                {/* Columns preview */}
                <div className="mt-2">
                  <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                    Key Columns ({rep.columns.length}):
                  </p>
                  <div className="flex items-center gap-1 flex-wrap mt-1">
                    {rep.columns.slice(0, 4).map((col) => (
                      <span
                        key={col}
                        className="text-[10.5px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 border border-slate-200/50"
                      >
                        {col}
                      </span>
                    ))}
                    {rep.columns.length > 4 && (
                      <span className="text-[10px] text-slate-400 font-medium">
                        +{rep.columns.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Card Footer Actions */}
              <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-[11px] text-slate-400">
                  Actions: {rep.actions.join(", ")}
                </span>
                <span className="font-bold text-[#b07d17] text-xs inline-flex items-center gap-1 group-hover:underline">
                  Launch Report
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportCatalogBrowser;
